import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import type { HoursReport } from '../../types';
import { formatDate } from '@/shared/utils/locale';
import './HoursReportDocument.styl';

/** Organisation data printed on the report header and footer. */
export type ReportOrganization = { name: string; legalName?: string | null; logoUrl?: string | null; email?: string | null; phone?: string | null; website?: string | null; taxId?: string | null };

type Props = { report: HoursReport; organization: ReportOrganization; palette: { hex: string }[] };

const hours = (minutes: number) => {
  const h = minutes / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
};

const weeksLabel = (weeks: number[]) => (weeks.length === 0 ? '—' : weeks.length === 1 ? `S${weeks[0]}` : `S${weeks[0]} – S${weeks[weeks.length - 1]}`);

/**
 * Printable hours report of one member (after the organisation's report model): header with the organisation's
 * logo, member data, distribution of the hours by work front, the tasks by front with their weeks, load per week,
 * notes and signatures. Colours come from the organisation's report palette.
 */
export function HoursReportDocument({ report, organization, palette }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'hoursReport' });
  const c = (i: number, fallback: string) => palette[i]?.hex ?? fallback;
  const vars = {
    '--rp-primary': c(0, '#164A66'),
    '--rp-primary-dark': c(1, '#123F57'),
    '--rp-pattern': c(2, '#18455D'),
    '--rp-accent': c(3, '#D1BAAA'),
    '--rp-accent-dark': c(4, '#C9B09E'),
    '--rp-accent-light': c(5, '#D8C1B2'),
    '--rp-base': c(6, '#F4F4F4')
  } as CSSProperties;
  const segmentColors = [c(0, '#164A66'), c(2, '#18455D'), c(4, '#C9B09E'), c(3, '#D1BAAA'), c(5, '#D8C1B2'), c(1, '#123F57')];
  const total = report.totalMinutes;
  const weeklyAvg = report.weeks.length > 0 ? total / report.weeks.length : 0;
  // row numbers run across the groups (01, 02, …)
  const offsets = report.groups.map((_, gi) => report.groups.slice(0, gi).reduce((n, g) => n + g.tasks.length, 0));

  return (
    <article className="f-hours-doc" style={vars} aria-label={t('title')}>
      <header className="f-hours-doc__header">
        {organization.logoUrl ? <img className="f-hours-doc__logo" src={organization.logoUrl} alt={organization.name} /> : <div className="f-hours-doc__org-name">{organization.name}</div>}
        <div className="f-hours-doc__heading">
          <div className="f-hours-doc__title">{t('title')}</div>
          <div className="f-hours-doc__subtitle">{t('subtitle', { hours: hours(total), from: formatDate(report.from), to: formatDate(report.to) })}</div>
        </div>
      </header>
      <div className="f-hours-doc__rule" />

      <section className="f-hours-doc__info">
        <div>
          <div className="f-hours-doc__label">{t('member')}</div>
          <div className="f-hours-doc__name">{report.member.name}</div>
          <div className="f-hours-doc__facts">
            {report.member.jobTitle ? (
              <div>
                <strong>{t('jobTitle')}:</strong> {report.member.jobTitle}
              </div>
            ) : null}
            {report.member.department ? (
              <div>
                <strong>{t('department')}:</strong> {report.member.department}
              </div>
            ) : null}
            {report.groups.length > 0 ? (
              <div>
                <strong>{t('fronts')}:</strong> {report.groups.map((g) => g.title || t('looseFront')).join(' · ')}
              </div>
            ) : null}
            <div>
              <strong>{t('modality')}:</strong> {t('modalityValue', { avg: hours(Math.round(weeklyAvg)), weeks: report.weeks.length })}
              {report.member.memberType || report.member.contractType ? ` · ${[report.member.memberType, report.member.contractType].filter(Boolean).join(' · ')}` : ''}
            </div>
          </div>
        </div>
        <table className="f-hours-doc__meta">
          <tbody>
            <tr>
              <th>{t('number')}</th>
              <td>
                <strong>{report.reportNumber}</strong>
              </td>
            </tr>
            <tr>
              <th>{t('issuedOn')}</th>
              <td>{formatDate(report.issuedOn)}</td>
            </tr>
            <tr>
              <th>{t('worked')}</th>
              <td>
                <strong>{hours(total)}</strong>
              </td>
            </tr>
            <tr>
              <th>{t('period')}</th>
              <td>
                {formatDate(report.from)} – {formatDate(report.to)}
              </td>
            </tr>
            <tr>
              <th>{t('status')}</th>
              <td>
                <span className="f-hours-doc__pill">{t('registered')}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {total > 0 ? (
        <section className="f-hours-doc__block">
          <div className="f-hours-doc__label">{t('distribution', { hours: hours(total) })}</div>
          <div className="f-hours-doc__bar">
            {report.groups.map((g, i) => (
              <span key={g.title} style={{ width: `${(g.minutes / total) * 100}%`, background: segmentColors[i % segmentColors.length] }} />
            ))}
          </div>
          <div className="f-hours-doc__legend">
            {report.groups.map((g, i) => (
              <span key={g.title}>
                <i style={{ background: segmentColors[i % segmentColors.length] }} />
                {g.title || t('looseFront')} · {hours(g.minutes)}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <table className="f-hours-doc__table">
        <thead>
          <tr>
            <th className="f-hours-doc__col-n">#</th>
            <th>{t('task')}</th>
            <th className="f-hours-doc__col-week">{t('week')}</th>
            <th className="f-hours-doc__col-hours">{t('hours')}</th>
          </tr>
        </thead>
        <tbody>
          {report.groups.length === 0 ? (
            <tr>
              <td colSpan={4} className="f-hours-doc__empty">
                {t('empty')}
              </td>
            </tr>
          ) : null}
          {report.groups.flatMap((g, gi) => [
            <tr key={`g-${g.title}`} className="f-hours-doc__group">
              <td colSpan={4}>
                {String.fromCharCode(65 + (gi % 26))}. {g.title || t('looseFront')} · {hours(g.minutes)}
              </td>
            </tr>,
            ...g.tasks.map((task, ti) => {
              const row = (offsets[gi] ?? 0) + ti + 1;
              return (
                <tr key={task.taskId} className={row % 2 === 0 ? 'f-hours-doc__row f-hours-doc__row--alt' : 'f-hours-doc__row'}>
                  <td className="f-hours-doc__n">{String(row).padStart(2, '0')}</td>
                  <td>
                    <div className="f-hours-doc__task">{task.title}</div>
                    {task.description ? <div className="f-hours-doc__desc">{task.description}</div> : null}
                  </td>
                  <td className="f-hours-doc__week">{weeksLabel(task.weeks)}</td>
                  <td className="f-hours-doc__h">{hours(task.minutes)}</td>
                </tr>
              );
            })
          ])}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>{t('total', { name: report.member.name })}</td>
            <td className="f-hours-doc__total">{hours(total)}</td>
          </tr>
        </tfoot>
      </table>

      <section className="f-hours-doc__summary">
        <div>
          <div className="f-hours-doc__label">{t('perWeek')}</div>
          {report.weeks.map((w) => (
            <div key={w.number} className="f-hours-doc__line">
              <span>
                {t('weekN', { n: w.number })} <small>({formatDate(w.from)} – {formatDate(w.to)})</small>
              </span>
              <span>{hours(w.minutes)}</span>
            </div>
          ))}
        </div>
        <div>
          <div className="f-hours-doc__label">{t('perFront')}</div>
          {report.groups.map((g) => (
            <div key={g.title} className="f-hours-doc__line">
              <span>{g.title || t('looseFront')}</span>
              <span>{hours(g.minutes)}</span>
            </div>
          ))}
          <div className="f-hours-doc__grand">
            <span>{t('grandTotal')}</span>
            <span>{hours(total)}</span>
          </div>
        </div>
      </section>

      <section className="f-hours-doc__notes">
        <div className="f-hours-doc__label">{t('notes')}</div>
        <ul>
          <li>
            <strong>{t('noteSourceTitle')}:</strong> {t('noteSource')}
          </li>
          <li>
            <strong>{t('noteWeeksTitle')}:</strong> {t('noteWeeks', { from: formatDate(report.from) })}
          </li>
        </ul>
      </section>

      <section className="f-hours-doc__signatures">
        <div>
          <span>
            {report.member.name}
            {report.member.jobTitle ? ` — ${report.member.jobTitle}` : ''}
          </span>
        </div>
        <div>
          <span>{t('approvedBy', { org: organization.legalName ?? organization.name })}</span>
        </div>
      </section>

      <footer className="f-hours-doc__footer">
        {[organization.legalName ?? organization.name, organization.taxId ? `RNC ${organization.taxId}` : null, organization.email, organization.phone, organization.website].filter(Boolean).join(' · ')}
      </footer>
    </article>
  );
}
